import { createNewService } from "@emergencias/jsclient";
import axios, { AxiosError } from "axios";
import {
  type AuthTokens,
  clearTokensLocalStorage,
  getLocalToken,
  setLocalToken,
} from "./LocalStorage";
import { redirectToLogin as redirectToLoginUtil } from "./authUtils";

let isLoggingOut = false;

const rbacApiUrl = import.meta.env.VITE_RBAC_API_URL;
const clientID = import.meta.env.VITE_RBAC_CLIENT_ID;
const validateTokensUrl = import.meta.env.VITE_CHAT_SERVICE_API_BASE_URL;

interface ErrorApi {
  errors: string[];
  isSuccessfull: boolean;
  message: string;
}

export type ITokens = AuthTokens;

const isAuthTokens = (value: unknown): value is AuthTokens => {
  if (typeof value !== "object" || value === null) return false;

  const tokens = value as Record<string, unknown>;

  return (
    typeof tokens.idToken === "string" &&
    typeof tokens.accessToken === "string" &&
    typeof tokens.infoToken === "string"
  );
};


const handleError = (error: unknown) => {
  if (axios.isAxiosError<ErrorApi>(error)) {

    const payload: AxiosError<ErrorApi> = error;
    if (payload.code === "ERR_NETWORK") {
      console.error(
        "No se pudo conectar con el servidor remoto.",
        "Ocurrió un error."
      );
    } else if (payload.response?.data.errors === undefined) {
      console.error(
        payload.response?.data.message ?? "",
        payload.response?.data.message ?? "Ocurrió un error."
      );
      //Si errors es undefined, intentar acceder a errors[0] lanza el error:
      //Cannot read properties of undefined (reading '0')
      throw error;
    } else if (payload.response?.data.errors !== undefined) {
      console.error(
        payload.response?.data.errors[0] ?? "",
        payload.response?.data.message ?? "Ocurrió un error."
      );
      throw error;
    }
  } else {
    console.error("Unexpected error: ", error);
    throw error;
  }
};

const authService = createNewService(rbacApiUrl, handleError, clientID);

export const useAuthService = () => {
  const {
    GetTokens,
    LogOut: logOut,
    RefreshTokens,
    ValidateResource: validateResource,
  } = authService.AuthService;

  const getTokens = async (code?: string | null): Promise<ITokens | null> => {
    const tokens = getLocalToken();
    if (code && !tokens) {
      await GetTokens({ code });
      return getLocalToken();
    }

    return tokens;
  };

  const refreshTokens = async (tokens: ITokens): Promise<ITokens> => {
    const refreshedTokens: unknown = await RefreshTokens(tokens);

    if (!isAuthTokens(refreshedTokens)) {
      throw new Error("La renovación no devolvió tokens válidos.");
    }

    setLocalToken(refreshedTokens);

    return refreshedTokens;
  };

  const validateTokens = async (tokens: ITokens | null) => {
    if (!tokens) {
      redirectToLogin();
    }

    const response = await axios.get(`${validateTokensUrl}/Auth/CheckToken`, {
      headers: {
        Authorization: JSON.stringify(tokens),
      },
    });

    if (response.status !== 200) throw new Error("Los tokens no son válidos.");
  };

  const deleteTokens = async () => {
    if (isLoggingOut) return; // Prevenir múltiples llamadas

    isLoggingOut = true;

    try {
      const tokens = getLocalToken();
      if (tokens) {
        await logOut(tokens.idToken);
        clearTokensLocalStorage();
        redirectToLogin();
      }
    } catch (error) {
      console.error('Error during logout:', error);
      clearTokensLocalStorage();
      redirectToLogin();
    } finally {
      isLoggingOut = false;
    }
  };

  const redirectToLogin = () => {
    redirectToLoginUtil();
  };

  return {
    getTokens,
    validateTokens,
    redirectToLogin,
    logOut,
    deleteTokens,
    refreshTokens,
    validateResource,
  };
};

// Exportar el flag para uso en interceptores
export const getIsLoggingOut = () => isLoggingOut;
